<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Canvas2DRenderer } from '../lib/render/canvas2d';
  import { attachPanZoom } from '../lib/render/interactions';
  import { cellAt, fitToScreen } from '../lib/render/viewport';
  import { centerViewportOn } from '../lib/render/minimap';
  import MiniMap from '../lib/components/MiniMap.svelte';
  import MobileDock from '../lib/components/MobileDock.svelte';
  import MobilePanel from '../lib/components/MobilePanel.svelte';
  import FabMenu from '../lib/components/FabMenu.svelte';
  import type {
    CurrentLine,
    RenderMode,
    RenderTheme,
    ToolPreview,
    Viewport as Vp,
  } from '../lib/render/types';
  import { imageToPattern, type Pattern } from '../lib/pipeline/pipeline';
  import { HAMA_MIDI_COLORS } from '../lib/catalog/colors';
  import { canonicalIndexById, sortColorsByHue } from '../lib/catalog/sort';
  import type { RgbColor } from '../lib/color/types';
  import type { Grid } from '../lib/quantize/types';
  import {
    applyStroke,
    getCellAt,
    paintCellsBatch,
    revertStroke,
  } from '../lib/edit/pattern-ops';
  import { floodFill } from '../lib/edit/flood-fill';
  import { brushCells } from '../lib/edit/brush';
  import { passesEraseFilter } from '../lib/edit/eraser-filter';
  import {
    canRedo as historyCanRedo,
    canUndo as historyCanUndo,
    push as historyPush,
    redo as historyRedo,
    undo as historyUndo,
  } from '../lib/edit/history';
  import { EMPTY_HISTORY, type CellEdit, type History } from '../lib/edit/types';
  import { fileToImage } from '../lib/import/image-bridge';
  import NewPatternDialog, { type DialogMode } from '../lib/components/NewPatternDialog.svelte';
  import ExportDialog from '../lib/components/ExportDialog.svelte';
  import BeadGuideDialog from '../lib/components/BeadGuideDialog.svelte';
  import { createAutosave, type AutosaveStatus } from '../lib/persistence/autosave';
  import { deserializePattern } from '../lib/persistence/serialize';
  import { migrateToCanonicalPalette } from '../lib/persistence/migrate';
  import { DRAFT_ID, defaultProjectName, loadDraft } from '../lib/persistence/draft';
  import { EMPTY } from '../lib/pattern/empty';

  type Tool = 'paint' | 'bucket' | 'eraser' | 'pan';
  type BrushSize = 1 | 3 | 5;
  /**
   * Mode de l'éditeur :
   *  - `edit` : éditeur classique avec tous les outils (création)
   *  - `guided` : pose guidée F5 — lecture seule, navigation plaque/ligne,
   *    UI minimaliste pour aider à poser physiquement les perles
   */
  type EditorMode = 'edit' | 'guided';
  type GuidedAxis = 'row' | 'col';

  let canvasEl: HTMLCanvasElement | undefined = $state();
  let containerEl: HTMLDivElement | undefined = $state();
  let renderer: Canvas2DRenderer | null = null;
  let detachInteractions: (() => void) | null = null;

  let mode = $state<RenderMode>('pearl');
  let showPlateBorders = $state(true);
  let viewport = $state<Vp>({ offsetX: 0, offsetY: 0, scale: 1 });
  let containerSize = $state({ w: 800, h: 600 });

  // === Mode pose guidée (F5) ============================================
  let editorMode = $state<EditorMode>('edit');
  // Plaque physique en cours de pose (col/row dans le layout multi-plaques)
  let guidedPlate = $state<{ col: number; row: number }>({ col: 0, row: 0 });
  // Index de la ligne/colonne courante DANS la plaque (0..plateSize-1)
  let guidedLineInPlate = $state(0);
  // Axe de progression : ligne par ligne (horizontale) ou colonne par colonne
  let guidedAxis = $state<GuidedAxis>('row');
  // Ouverture du « Guide des perles » — modale qui liste tout le catalogue
  // catalogue Hama Midi (utile à tout moment pour identifier une couleur).
  let beadGuideOpen = $state(false);

  // Outil par défaut = main (pan). Évite les peintures accidentelles à
  // l'ouverture du patron : l'utilisateur doit explicitement choisir un outil
  // d'édition avant de pouvoir modifier des perles.
  let tool = $state<Tool>('pan');
  let brushSize = $state<BrushSize>(1);
  let activeColorIdx = $state(6); // hama-205-08 par défaut (Bleu)
  let history = $state<History>(EMPTY_HISTORY);

  // La gomme retire la perle (cellule devient EMPTY) plutôt que de la peindre
  // en blanc — comportement attendu d'un outil « gomme » au sens propre. Le
  // fond de la plaque (canvasBg) est visible à la place de la perle.
  const ERASER_COLOR_IDX = EMPTY;

  // Hover sur le canvas — pour afficher le preview de l'outil.
  let hoverCol = $state<number | null>(null);
  let hoverRow = $state<number | null>(null);

  // Ouverture du panneau d'options mobile (bottom-sheet)
  let mobilePanelOpen = $state(false);

  // Fond du canvas — par défaut blanc (réaliste : la vraie plaque Hama est blanche).
  type CanvasBgMode = 'white' | 'brand' | 'custom';
  let canvasBgMode = $state<CanvasBgMode>('white');
  let canvasBgCustom = $state('#1a1a1a'); // gris foncé en cas de switch en custom
  let colorInputEl: HTMLInputElement | undefined = $state();
  function openColorPicker(): void {
    canvasBgMode = 'custom';
    // Petit timeout pour laisser le state se mettre à jour avant d'ouvrir le picker
    setTimeout(() => colorInputEl?.click(), 0);
  }

  // --- Création nouveau patron (import / vierge) ------------------------
  let fileInputEl: HTMLInputElement | undefined = $state();
  let importStatus = $state<'idle' | 'loading' | 'error'>('idle');
  let importError = $state('');

  let dialogOpen = $state(false);
  let dialogMode = $state<DialogMode>('blank');
  let dialogSource = $state<HTMLImageElement | undefined>(undefined);

  let exportDialogOpen = $state(false);

  /**
   * Déclenche le picker natif iOS / Android. DOIT être 100% synchrone :
   * iOS Safari bloque silencieusement `input.click()` si un `await` est
   * intercalé entre le gesture utilisateur et l'appel — il faut donc PAS
   * de check async ici.
   */
  function openFilePicker(): void {
    fileInputEl?.click();
  }

  function openBlankDialog(): void {
    // Pas de contrainte iOS sur l'ouverture d'un dialog HTML, mais on garde
    // la fonction synchrone pour cohérence.
    dialogSource = undefined;
    dialogMode = 'blank';
    dialogOpen = true;
  }

  /** Jeton de génération : deux sélections rapprochées peuvent se décoder
   *  dans le désordre (gros JPEG lent) — seule la DERNIÈRE doit s'appliquer. */
  let importGen = 0;

  async function onFileSelected(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const gen = ++importGen;
    importStatus = 'loading';
    importError = '';
    try {
      const img = await fileToImage(file);
      if (gen !== importGen) return; // sélection périmée — une photo plus récente a gagné
      dialogSource = img;
      dialogMode = 'import';
      dialogOpen = true;
      importStatus = 'idle';
    } catch (err) {
      if (gen !== importGen) return;
      // Surface l'erreur en console pour pouvoir diagnostiquer les crashs
      // mobile (signalés par l'utilisateur, sans repro stable pour l'instant).
      console.error('[import] Échec du chargement de la photo :', err);
      importStatus = 'error';
      importError = err instanceof Error ? err.message : 'Erreur inconnue.';
    } finally {
      input.value = ''; // permet de réimporter le même fichier ensuite
    }
  }

  function onDialogCancel(): void {
    dialogOpen = false;
    dialogSource = undefined;
  }

  function onDialogGenerate(newPattern: Pattern): void {
    // Brouillon unique post-pivot : générer un patron REMPLACE le brouillon
    // courant (plus de multi-projets ni de cap — le client configure un kit,
    // il n'en collectionne pas).
    //
    // `initialLoadDone = true` AVANT les assignations : (1) neutralise un
    // tryRestore encore en vol (sinon il écraserait le patron fraîchement
    // généré avec l'ancien brouillon — course réelle sur IDB lente), et
    // (2) arme le $effect autosave, qui persiste le nouveau brouillon dès
    // le flush (pas besoin de trigger explicite ici).
    initialLoadDone = true;
    pattern = newPattern;
    history = EMPTY_HISTORY;
    projectName = defaultProjectName();
    dialogOpen = false;
    dialogSource = undefined;
    importStatus = 'idle';
    importError = '';
    setTimeout(() => fitView(), 0);
  }

  // --- Autosave + restore ---------------------------------------------
  // Un seul brouillon, clé IDB fixe (cf. persistence/draft.ts). Le {#key}
  // App.svelte et l'id d'URL ont disparu avec le multi-projets.
  let projectName = $state<string>(defaultProjectName());
  const autosave = createAutosave(DRAFT_ID, 1000);
  let autosaveStatus = $state<AutosaveStatus>('idle');
  let unsubAutosave: (() => void) | null = null;
  let lastSavedAt = $state(0);
  let initialLoadDone = false;

  async function tryRestore(): Promise<void> {
    try {
      const record = await loadDraft();
      if (initialLoadDone) {
        // Le user a généré un patron pendant le chargement : sa création
        // gagne sur le brouillon restauré. On re-persiste par sécurité —
        // l'adoption d'un record hérité (cf. loadDraft) a pu écrire
        // l'ancien patron sous la clé 'draft' entre-temps.
        autosave.trigger(pattern, projectName);
        return;
      }
      if (record) {
        // Migration défensive : remap des cells si paletteColorIds n'est pas
        // dans l'ordre canonique HAMA (cas des patrons générés brièvement
        // avec un ordre trié par teinte — voir migrate.ts). No-op si déjà OK.
        pattern = migrateToCanonicalPalette(
          deserializePattern(record.pattern),
          HAMA,
        );
        projectName = record.name;
        history = EMPTY_HISTORY;
        setTimeout(() => fitView(), 0);
      } else {
        // Aucun brouillon : reste sur le pattern de démo, nom par défaut.
        // L'autosave persistera tout dès la première modification.
        projectName = defaultProjectName();
      }
    } catch (err) {
      console.error('[restore] Échec de la restauration IndexedDB :', err);
    } finally {
      initialLoadDone = true;
    }
  }

  function toolLabel(t: Tool): string {
    switch (t) {
      case 'paint':
        return 'pinceau';
      case 'bucket':
        return 'pot de peinture';
      case 'eraser':
        return 'gomme';
      case 'pan':
        return 'main';
    }
  }
  function toolHint(t: Tool): string {
    switch (t) {
      case 'paint':
        return 'Clic+glisser pour peindre · molette pour zoomer · Ctrl+Z';
      case 'bucket':
        return 'Clic pour remplir une zone de même couleur · molette pour zoomer';
      case 'eraser':
        return 'Clic+glisser pour effacer · molette pour zoomer · Ctrl+Z';
      case 'pan':
        return 'Clic+glisser pour déplacer · molette pour zoomer';
    }
  }

  function statusLabel(s: AutosaveStatus): string {
    switch (s) {
      case 'idle':
        return '';
      case 'pending':
        return 'Modifications non sauvées';
      case 'saving':
        return 'Enregistrement…';
      case 'saved': {
        const ago = Math.round((Date.now() - lastSavedAt) / 1000);
        return ago < 5 ? 'Enregistré' : `Enregistré il y a ${ago} s`;
      }
      case 'error':
        return 'Sauvegarde indisponible';
    }
  }

  // Buffer du stroke en cours (entre mousedown et mouseup).
  // Pour la perf, on ne mute PAS `pattern` pendant le drag : on accumule les
  // edits + on stocke un overlay `dragOverrides` que le renderer utilise pour
  // afficher en temps réel. Au mouseup, un seul `paintCellsBatch` applique
  // tout en une fois (1 clone de cells[] de 90k+ entrées, au lieu de N).
  let currentStroke: CellEdit[] = [];
  let dragOverrides = new Map<number, number>(); // key = row*width+col → newIdx
  let isPainting = false;

  const CELL_SIZE = 20;
  const HAMA = HAMA_MIDI_COLORS;
  // Ordre d'affichage par teinte (neutres en tête, puis chromatiques sur la
  // roue). NE remplace PAS HAMA pour le rendu canvas — les `pattern.cells[]`
  // référencent l'index canonique dans HAMA, qui reste figé.
  const HAMA_DISPLAY = sortColorsByHue(HAMA_MIDI_COLORS);
  const HAMA_CANON = canonicalIndexById(HAMA_MIDI_COLORS);

  function makeFacePixels(N: number): RgbColor[] {
    const pixels: RgbColor[] = [];
    const center = (N - 1) / 2;
    for (let r = 0; r < N; r++) {
      for (let c = 0; c < N; c++) {
        const dx = c - center;
        const dy = r - center;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let p: RgbColor;
        if (r < 6 && Math.abs(dx) < 8) p = { r: 92, g: 46, b: 29 };
        else if (dist > 13) p = { r: 184, g: 216, b: 240 };
        else if (r === 13 && (dx === -5 || dx === 5)) p = { r: 0, g: 0, b: 0 };
        else if (r === 17 && Math.abs(dx) < 3) p = { r: 236, g: 32, b: 39 };
        else p = { r: 244, g: 220, b: 177 };
        pixels.push(p);
      }
    }
    return pixels;
  }

  function makeDemoPattern(): Pattern {
    const N = 29;
    const grid: Grid = { width: N, height: N, pixels: makeFacePixels(N) };
    return imageToPattern(grid, {
      targetWidth: N,
      targetHeight: N,
      palette: HAMA,
      resampling: 'nearest',
      quantization: 'nearest',
      plate: { width: 29, height: 29 },
    });
  }

  let pattern = $state<Pattern>(makeDemoPattern());

  function readBrandSurface(): string {
    if (typeof document === 'undefined') return '#E3DCCE';
    const cs = getComputedStyle(document.documentElement);
    return cs.getPropertyValue('--color-surface').trim() || '#E3DCCE';
  }

  function readTheme(): RenderTheme {
    if (typeof document === 'undefined') {
      return { plateBorder: '#682A37', plateActive: '#B8843F', beadHole: canvasBg };
    }
    const cs = getComputedStyle(document.documentElement);
    const get = (name: string, fallback: string): string =>
      cs.getPropertyValue(name).trim() || fallback;
    return {
      plateBorder: get('--color-primary', '#682A37'),
      plateActive: get('--color-accent', '#B8843F'),
      // Le trou de la perle correspond à la couleur de fond (réaliste : sur une
      // vraie plaque, le trou laisse voir la plaque elle-même).
      beadHole: canvasBg,
    };
  }

  function fitView(): void {
    viewport = fitToScreen(
      pattern.width * CELL_SIZE,
      pattern.height * CELL_SIZE,
      containerSize.w,
      containerSize.h,
    );
  }

  function onMiniMapPan(col: number, row: number): void {
    viewport = centerViewportOn(viewport, col, row, containerSize, CELL_SIZE);
  }

  function draw(): void {
    if (!renderer) return;
    renderer.draw({
      pattern,
      palette: HAMA,
      viewport,
      mode,
      showPlateBorders,
      theme: readTheme(),
      cellSize: CELL_SIZE,
      ...(dragOverrides.size > 0 ? { overrides: dragOverrides } : {}),
      // Pas de preview pendant un drag : éviterait de flasher en plein paint.
      ...(toolPreview !== undefined && !isPainting ? { toolPreview } : {}),
      ...(currentPlate !== undefined ? { currentPlate } : {}),
      ...(guidedCurrentLine !== undefined ? { currentLine: guidedCurrentLine } : {}),
    });
  }

  /**
   * Coalesce les demandes de redraw dans un seul `requestAnimationFrame`.
   * Critique pour les grandes grilles : sans ça, un pan déclenche 60+ redraws
   * complets / seconde, ce qui sature le CPU sur 90 000+ perles.
   */
  let rafId = 0;
  function scheduleDraw(): void {
    if (rafId !== 0) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      draw();
    });
  }

  // --- Painting --------------------------------------------------------

  /**
   * Couleur cible selon l'outil actif (paint = activeColorIdx, eraser = 0/blanc).
   * `bucket` est géré séparément (single hit).
   */
  function targetColorIdx(): number {
    return tool === 'eraser' ? ERASER_COLOR_IDX : activeColorIdx;
  }

  /** Peint UNE cellule (col, row) dans le buffer du drag courant. */
  function paintCellInDrag(col: number, row: number, targetIdx: number): void {
    const key = row * pattern.width + col;
    if (dragOverrides.has(key)) return; // anti-doublon
    const baseIdx = getCellAt(pattern, col, row);
    if (baseIdx === undefined || baseIdx === targetIdx) return;
    // Gomme à filtre : si une couleur est sélectionnée en palette pendant
    // l'usage de la gomme, n'efface que les cellules de cette couleur dans
    // la footprint du pinceau (utile pour nettoyer un bleed sans toucher
    // aux voisines).
    if (tool === 'eraser' && !passesEraseFilter(baseIdx, activeColorIdx)) return;
    dragOverrides.set(key, targetIdx);
    currentStroke.push({ col, row, oldIdx: baseIdx, newIdx: targetIdx });
  }

  function paintAtScreen(screenX: number, screenY: number): void {
    const hit = cellAt(viewport, screenX, screenY, CELL_SIZE, pattern.width, pattern.height);
    if (!hit) return;
    const targetIdx = targetColorIdx();
    // Pinceau / gomme : applique le brush size (carré centré).
    const cells = brushCells(hit.col, hit.row, brushSize, pattern.width, pattern.height);
    for (const c of cells) paintCellInDrag(c.col, c.row, targetIdx);
    scheduleDraw();
  }

  /** Action ponctuelle du pot de peinture (un seul clic = un seul stroke). */
  function bucketAtScreen(screenX: number, screenY: number): void {
    const hit = cellAt(viewport, screenX, screenY, CELL_SIZE, pattern.width, pattern.height);
    if (!hit) return;
    const baseIdx = getCellAt(pattern, hit.col, hit.row);
    if (baseIdx === undefined || baseIdx === activeColorIdx) return;
    const cellsToFill = floodFill(pattern, hit.col, hit.row);
    if (cellsToFill.length === 0) return;
    const stroke: CellEdit[] = cellsToFill.map((c) => ({
      col: c.col,
      row: c.row,
      oldIdx: baseIdx,
      newIdx: activeColorIdx,
    }));
    pattern = paintCellsBatch(pattern, stroke);
    history = historyPush(history, stroke);
    scheduleDraw();
  }

  function canvasLocal(clientX: number, clientY: number): { x: number; y: number } {
    if (!canvasEl) return { x: 0, y: 0 };
    const r = canvasEl.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  }

  function commitStroke(): void {
    if (currentStroke.length > 0) {
      // Une seule allocation cells[] + counts pour tout le stroke.
      pattern = paintCellsBatch(pattern, currentStroke);
      history = historyPush(history, currentStroke);
    }
    currentStroke = [];
    dragOverrides = new Map();
    scheduleDraw();
  }

  function isDrawingTool(): boolean {
    return tool === 'paint' || tool === 'eraser';
  }

  function onPaintMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return;
    if (tool === 'bucket') {
      const p = canvasLocal(e.clientX, e.clientY);
      bucketAtScreen(p.x, p.y);
      return;
    }
    if (!isDrawingTool()) return;
    isPainting = true;
    currentStroke = [];
    dragOverrides = new Map();
    const p = canvasLocal(e.clientX, e.clientY);
    paintAtScreen(p.x, p.y);
  }
  function onPaintMouseMove(e: MouseEvent): void {
    if (!isPainting) return;
    const p = canvasLocal(e.clientX, e.clientY);
    paintAtScreen(p.x, p.y);
  }
  function onPaintMouseUp(): void {
    if (!isPainting) return;
    isPainting = false;
    commitStroke();
  }

  function onPaintTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    if (!t) return;
    if (tool === 'bucket') {
      e.preventDefault();
      const p = canvasLocal(t.clientX, t.clientY);
      bucketAtScreen(p.x, p.y);
      return;
    }
    if (!isDrawingTool()) return;
    e.preventDefault();
    isPainting = true;
    currentStroke = [];
    dragOverrides = new Map();
    const p = canvasLocal(t.clientX, t.clientY);
    paintAtScreen(p.x, p.y);
  }
  function onPaintTouchMove(e: TouchEvent): void {
    if (!isPainting || e.touches.length !== 1) return;
    const t = e.touches[0];
    if (!t) return;
    e.preventDefault();
    const p = canvasLocal(t.clientX, t.clientY);
    paintAtScreen(p.x, p.y);
  }
  function onPaintTouchEnd(): void {
    if (!isPainting) return;
    isPainting = false;
    commitStroke();
  }

  // --- Hover (preview de l'outil) -------------------------------------

  function updateHoverFromScreen(screenX: number, screenY: number): void {
    const hit = cellAt(viewport, screenX, screenY, CELL_SIZE, pattern.width, pattern.height);
    if (!hit) {
      hoverCol = null;
      hoverRow = null;
      return;
    }
    hoverCol = hit.col;
    hoverRow = hit.row;
  }

  function onHoverMove(e: MouseEvent): void {
    if (tool === 'pan') {
      hoverCol = null;
      hoverRow = null;
      return;
    }
    const p = canvasLocal(e.clientX, e.clientY);
    updateHoverFromScreen(p.x, p.y);
  }

  function onHoverLeave(): void {
    hoverCol = null;
    hoverRow = null;
  }

  // --- Undo / Redo -----------------------------------------------------

  function doUndo(): void {
    const r = historyUndo(history);
    if (!r.stroke) return;
    pattern = revertStroke(pattern, r.stroke);
    history = r.history;
  }
  function doRedo(): void {
    const r = historyRedo(history);
    if (!r.stroke) return;
    pattern = applyStroke(pattern, r.stroke);
    history = r.history;
  }

  function onKeyDown(e: KeyboardEvent): void {
    const isCtrl = e.ctrlKey || e.metaKey;
    if (!isCtrl) return;
    if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      doUndo();
    } else if ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y') {
      e.preventDefault();
      doRedo();
    }
  }

  // --- Lifecycle -------------------------------------------------------

  onMount(() => {
    if (!canvasEl || !containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    canvasEl.width = rect.width;
    canvasEl.height = rect.height;
    containerSize = { w: rect.width, h: rect.height };

    const ctx = canvasEl.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context indisponible.');
    renderer = new Canvas2DRenderer(ctx, canvasEl.width, canvasEl.height);
    fitView();

    // Tente de restaurer le dernier projet sauvegardé (graceful si IDB indispo).
    void tryRestore();

    unsubAutosave = autosave.onStatusChange((s) => {
      autosaveStatus = s;
      if (s === 'saved') lastSavedAt = autosave.getLastSavedAt();
    });

    detachInteractions = attachPanZoom(canvasEl, () => viewport, (v) => (viewport = v), {
      shouldPanOnMouseDrag: () => tool === 'pan',
      shouldPanOnTouchDrag: () => tool === 'pan',
    });

    // Paint handlers
    canvasEl.addEventListener('mousedown', onPaintMouseDown);
    window.addEventListener('mousemove', onPaintMouseMove);
    window.addEventListener('mouseup', onPaintMouseUp);
    canvasEl.addEventListener('touchstart', onPaintTouchStart, { passive: false });
    canvasEl.addEventListener('touchmove', onPaintTouchMove, { passive: false });
    canvasEl.addEventListener('touchend', onPaintTouchEnd);
    canvasEl.addEventListener('touchcancel', onPaintTouchEnd);

    // Hover (preview de l'outil au survol — pinceau / gomme / pot)
    canvasEl.addEventListener('mousemove', onHoverMove);
    canvasEl.addEventListener('mouseleave', onHoverLeave);

    window.addEventListener('keydown', onKeyDown);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !canvasEl) return;
      const r = entry.contentRect;
      canvasEl.width = r.width;
      canvasEl.height = r.height;
      containerSize = { w: r.width, h: r.height };
      renderer?.resize(r.width, r.height);
      scheduleDraw();
    });
    ro.observe(containerEl);

    return () => {
      ro.disconnect();
    };
  });

  onDestroy(() => {
    detachInteractions?.();
    if (canvasEl) {
      canvasEl.removeEventListener('mousedown', onPaintMouseDown);
      canvasEl.removeEventListener('touchstart', onPaintTouchStart);
      canvasEl.removeEventListener('touchmove', onPaintTouchMove);
      canvasEl.removeEventListener('touchend', onPaintTouchEnd);
      canvasEl.removeEventListener('touchcancel', onPaintTouchEnd);
      canvasEl.removeEventListener('mousemove', onHoverMove);
      canvasEl.removeEventListener('mouseleave', onHoverLeave);
    }
    window.removeEventListener('mousemove', onPaintMouseMove);
    window.removeEventListener('mouseup', onPaintMouseUp);
    window.removeEventListener('keydown', onKeyDown);
    unsubAutosave?.();
    void autosave.flush(); // sauve les modifs en attente avant destruction
  });

  $effect(() => {
    void pattern;
    void viewport;
    void mode;
    void showPlateBorders;
    void canvasBg;
    void toolPreview;
    void currentPlate;
    void guidedCurrentLine;
    scheduleDraw();
  });

  // Autosave debouncé sur les changements de pattern OU de nom de projet
  // (uniquement après le load initial, pour ne pas sauvegarder le pattern de
  // démo par-dessus le projet restauré).
  $effect(() => {
    void pattern;
    void projectName;
    if (!initialLoadDone) return;
    autosave.trigger(pattern, projectName);
  });

  // --- Derived -------------------------------------------------------

  let activeColor = $derived(HAMA[activeColorIdx]);
  let canUndo = $derived(historyCanUndo(history));
  let canRedo = $derived(historyCanRedo(history));

  let canvasBg = $derived(
    canvasBgMode === 'white' ? '#FFFFFF' : canvasBgMode === 'brand' ? readBrandSurface() : canvasBgCustom,
  );

  /**
   * Preview de l'outil : ce que ferait un clic à la position de hover courante.
   * Recalculé via $derived ; pour le pot, le flood fill peut être coûteux mais
   * ne re-run que si une dep change (hoverCol/Row, tool, pattern).
   */
  let toolPreview = $derived.by<ToolPreview | undefined>(() => {
    if (hoverCol === null || hoverRow === null) return undefined;
    if (tool === 'paint' || tool === 'eraser') {
      const r = Math.floor(brushSize / 2);
      return {
        kind: 'brush-rect' as const,
        minCol: Math.max(0, hoverCol - r),
        minRow: Math.max(0, hoverRow - r),
        maxCol: Math.min(pattern.width - 1, hoverCol + r),
        maxRow: Math.min(pattern.height - 1, hoverRow + r),
      };
    }
    if (tool === 'bucket') {
      try {
        const cells = floodFill(pattern, hoverCol, hoverRow);
        return { kind: 'fill-cells' as const, cells };
      } catch {
        return undefined;
      }
    }
    return undefined;
  });

  /**
   * Plaque physique survolée par le curseur (col/row dans le layout multi-plaques).
   * Gating sur `showPlateBorders` : si le user a masqué les bords, on n'affiche
   * pas non plus le highlight (cohérence visuelle). Pas de surimpression sur les
   * grilles single-plate (cols×rows == 1) où l'highlight serait redondant.
   */
  let currentPlate = $derived.by<{ col: number; row: number } | undefined>(() => {
    // En mode pose guidée, la plaque est imposée par la navigation (pas par le hover).
    if (editorMode === 'guided') return guidedPlate;
    if (!showPlateBorders) return undefined;
    if (hoverCol === null || hoverRow === null) return undefined;
    const { plateWidth, plateHeight, cols, rows } = pattern.layout;
    if (plateWidth <= 0 || plateHeight <= 0) return undefined;
    if (cols <= 1 && rows <= 1) return undefined;
    return {
      col: Math.floor(hoverCol / plateWidth),
      row: Math.floor(hoverRow / plateHeight),
    };
  });

  // === Derived pour la pose guidée ===
  // Total de plaques physiques (multi-plaques) — au moins 1.
  let totalPlates = $derived(pattern.layout.cols * pattern.layout.rows);
  // Index linéaire en row-major de la plaque courante (0-based).
  let currentPlateIndex = $derived(guidedPlate.row * pattern.layout.cols + guidedPlate.col);
  // Longueur de l'axe courant DANS la plaque (en cellules, jamais 0).
  let axisLengthInPlate = $derived(
    guidedAxis === 'row' ? pattern.layout.plateHeight : pattern.layout.plateWidth,
  );
  // Étendue de la plaque physique en coordonnées world (cellules).
  let plateBoundsWorld = $derived.by(() => {
    const { plateWidth, plateHeight } = pattern.layout;
    const minCol = guidedPlate.col * plateWidth;
    const minRow = guidedPlate.row * plateHeight;
    return {
      minCol,
      minRow,
      maxCol: Math.min(pattern.width - 1, minCol + plateWidth - 1),
      maxRow: Math.min(pattern.height - 1, minRow + plateHeight - 1),
    };
  });
  // Ligne/colonne courante à passer au renderer en mode guidé.
  let guidedCurrentLine = $derived.by<CurrentLine | undefined>(() => {
    if (editorMode !== 'guided') return undefined;
    const b = plateBoundsWorld;
    if (guidedAxis === 'row') {
      const row = b.minRow + guidedLineInPlate;
      if (row > b.maxRow) return undefined;
      return { axis: 'row', row, minCol: b.minCol, maxCol: b.maxCol };
    }
    const col = b.minCol + guidedLineInPlate;
    if (col > b.maxCol) return undefined;
    return { axis: 'col', col, minRow: b.minRow, maxRow: b.maxRow };
  });
  // Liste des perles à poser sur la ligne courante (col, row, beadColor).
  let currentLineBeads = $derived.by(() => {
    if (editorMode !== 'guided') return [];
    const b = plateBoundsWorld;
    const beads: Array<{ col: number; row: number; idx: number }> = [];
    if (guidedAxis === 'row') {
      const row = b.minRow + guidedLineInPlate;
      if (row > b.maxRow) return [];
      for (let col = b.minCol; col <= b.maxCol; col++) {
        const idx = pattern.cells[row * pattern.width + col];
        if (idx === undefined) continue;
        beads.push({ col, row, idx });
      }
    } else {
      const col = b.minCol + guidedLineInPlate;
      if (col > b.maxCol) return [];
      for (let row = b.minRow; row <= b.maxRow; row++) {
        const idx = pattern.cells[row * pattern.width + col];
        if (idx === undefined) continue;
        beads.push({ col, row, idx });
      }
    }
    return beads;
  });

  /**
   * Run-length encoding de `currentLineBeads` : regroupe les perles
   * **consécutives** de même couleur en un seul "run" affiché.
   *
   * Préserve l'ordre de la ligne — si la même couleur réapparaît plus loin
   * dans la ligne après une rupture, elle compte comme un run séparé (sinon
   * l'utilisateur poserait dans le mauvais ordre). Ex pour [Saumon×12,
   * Gris×3, Saumon×13] : 3 runs, pas 2.
   *
   * `startPos`/`endPos` sont en 1-based pour s'afficher tels quels.
   */
  type BeadRun = { idx: number; startPos: number; endPos: number; count: number };
  let currentLineRuns = $derived.by<BeadRun[]>(() => {
    const beads = currentLineBeads;
    if (beads.length === 0) return [];
    const first = beads[0];
    if (first === undefined) return [];
    const runs: BeadRun[] = [];
    let cur: BeadRun = { idx: first.idx, startPos: 1, endPos: 1, count: 1 };
    for (let i = 1; i < beads.length; i++) {
      const b = beads[i];
      if (b === undefined) continue;
      if (b.idx === cur.idx) {
        cur.endPos = i + 1;
        cur.count += 1;
      } else {
        runs.push(cur);
        cur = { idx: b.idx, startPos: i + 1, endPos: i + 1, count: 1 };
      }
    }
    runs.push(cur);
    return runs;
  });
  let hasPrevPlate = $derived(currentPlateIndex > 0);
  let hasNextPlate = $derived(currentPlateIndex < totalPlates - 1);
  let hasPrevLine = $derived(guidedLineInPlate > 0 || hasPrevPlate);
  let hasNextLine = $derived(guidedLineInPlate < axisLengthInPlate - 1 || hasNextPlate);

  /**
   * Recadre le viewport sur la plaque indiquée. Calcule le zoom + offset pour
   * que la plaque tienne dans le container avec une marge confortable.
   */
  function zoomToPlate(plateCol: number, plateRow: number): void {
    const { plateWidth, plateHeight } = pattern.layout;
    if (plateWidth <= 0 || plateHeight <= 0) return;
    const worldX = plateCol * plateWidth * CELL_SIZE;
    const worldY = plateRow * plateHeight * CELL_SIZE;
    const worldW = plateWidth * CELL_SIZE;
    const worldH = plateHeight * CELL_SIZE;
    const margin = 0.82;
    const scaleX = (containerSize.w * margin) / worldW;
    const scaleY = (containerSize.h * margin) / worldH;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (containerSize.w / scale - worldW) / 2 - worldX;
    const offsetY = (containerSize.h / scale - worldH) / 2 - worldY;
    viewport = { offsetX, offsetY, scale };
  }

  function enterGuidedMode(): void {
    editorMode = 'guided';
    guidedPlate = { col: 0, row: 0 };
    guidedLineInPlate = 0;
    // Double RAF : laisse Svelte appliquer le re-render ({#if}) + le
    // ResizeObserver mettre à jour `containerSize.w` au nouveau width
    // (workspace passe de 220+1fr à 1fr) avant de calculer le zoom plaque.
    requestAnimationFrame(() => requestAnimationFrame(() => zoomToPlate(0, 0)));
  }
  function exitGuidedMode(): void {
    editorMode = 'edit';
    requestAnimationFrame(() => requestAnimationFrame(() => fitView()));
  }
  function gotoPlate(idx: number): void {
    const clamped = Math.max(0, Math.min(totalPlates - 1, idx));
    const cols = pattern.layout.cols;
    const newPlate = { col: clamped % cols, row: Math.floor(clamped / cols) };
    guidedPlate = newPlate;
    guidedLineInPlate = 0;
    // Double RAF : si Safari mobile vient de bouger son URL bar ou si la
    // strip a re-rendu, on attend que ResizeObserver mette à jour
    // containerSize avant de zoomer (sinon zoom calculé sur ancienne taille).
    requestAnimationFrame(() =>
      requestAnimationFrame(() => zoomToPlate(newPlate.col, newPlate.row)),
    );
  }
  function nextPlate(): void {
    if (hasNextPlate) gotoPlate(currentPlateIndex + 1);
  }
  function prevPlate(): void {
    if (hasPrevPlate) gotoPlate(currentPlateIndex - 1);
  }
  function nextLine(): void {
    if (guidedLineInPlate < axisLengthInPlate - 1) {
      guidedLineInPlate += 1;
    } else if (hasNextPlate) {
      // Au-delà de la dernière ligne, on passe à la première de la plaque suivante.
      gotoPlate(currentPlateIndex + 1);
    }
  }
  function prevLine(): void {
    if (guidedLineInPlate > 0) {
      guidedLineInPlate -= 1;
    } else if (hasPrevPlate) {
      gotoPlate(currentPlateIndex - 1);
      guidedLineInPlate = axisLengthInPlate - 1;
    }
  }
  // Changer d'axe (ligne/colonne) reset la position à 0 — sinon l'index n'a
  // pas le même sens (la ligne 5 d'une plaque 29x29 ≠ la colonne 5).
  function setGuidedAxis(a: GuidedAxis): void {
    if (guidedAxis === a) return;
    guidedAxis = a;
    guidedLineInPlate = 0;
  }
</script>

<section class="editor" class:editor--guided={editorMode === 'guided'}>
  {#if editorMode === 'guided'}
    <header class="guided-bar">
      <!-- Ligne 1 : actions globales (Retour à gauche, titre au centre, Guide à droite). -->
      <div class="guided-bar__row guided-bar__row--top">
        <button
          class="guided-bar__back"
          type="button"
          onclick={exitGuidedMode}
          aria-label="Retour à l'édition"
        >
          ← Retour
        </button>
        <div class="guided-bar__title">
          <span class="guided-bar__eyebrow">Pose guidée</span>
          <strong class="guided-bar__name">{projectName || 'Sans titre'}</strong>
        </div>
        <button
          class="guided-bar__guide"
          type="button"
          title="Ouvrir le guide complet des perles Hama"
          onclick={() => (beadGuideOpen = true)}
        >
          ☆ Guide
        </button>
      </div>

      <!-- Ligne 2 : choix du mode de progression. Sur sa propre ligne pour
           accommoder l'ajout futur d'un troisième mode (pose par couleur). -->
      <div class="guided-bar__row guided-bar__row--axis">
        <div class="guided-nav__axis" role="group" aria-label="Sens de progression">
          <button
            type="button"
            class="guided-nav__axis-btn"
            class:active={guidedAxis === 'row'}
            aria-pressed={guidedAxis === 'row'}
            onclick={() => setGuidedAxis('row')}
            title="Progression ligne par ligne"
          >⇆ Ligne</button>
          <button
            type="button"
            class="guided-nav__axis-btn"
            class:active={guidedAxis === 'col'}
            aria-pressed={guidedAxis === 'col'}
            onclick={() => setGuidedAxis('col')}
            title="Progression colonne par colonne"
          >⇅ Colonne</button>
        </div>
      </div>

      <!-- Ligne 3 : nav plaque / ligne — les deux groupes côte à côte. -->
      <div class="guided-bar__row guided-bar__row--nav">
        <div class="guided-nav__group">
          <span class="guided-nav__label">Plaque</span>
          <strong class="guided-nav__value">{currentPlateIndex + 1}/{totalPlates}</strong>
          <button
            class="guided-nav__btn"
            type="button"
            onclick={prevPlate}
            disabled={!hasPrevPlate}
            aria-label="Plaque précédente"
          >◀</button>
          <button
            class="guided-nav__btn"
            type="button"
            onclick={nextPlate}
            disabled={!hasNextPlate}
            aria-label="Plaque suivante"
          >▶</button>
        </div>
        <div class="guided-nav__group">
          <span class="guided-nav__label">{guidedAxis === 'row' ? 'Ligne' : 'Colonne'}</span>
          <strong class="guided-nav__value">{guidedLineInPlate + 1}/{axisLengthInPlate}</strong>
          <button
            class="guided-nav__btn"
            type="button"
            onclick={prevLine}
            disabled={!hasPrevLine}
            aria-label="Précédent"
          >◀</button>
          <button
            class="guided-nav__btn"
            type="button"
            onclick={nextLine}
            disabled={!hasNextLine}
            aria-label="Suivant"
          >▶</button>
        </div>
      </div>
    </header>
  {/if}

  {#if editorMode === 'edit'}
  <header class="toolbar is-desktop-only">
    <div class="project-title">
      <span class="project-title__eyebrow">Patron</span>
      <input
        class="project-title__input"
        type="text"
        bind:value={projectName}
        placeholder="Sans titre"
        aria-label="Nom du patron"
        maxlength="80"
      />
    </div>
    <div class="groups">
      <!-- Création : deux entrées (photo / vierge) -->
      <button
        class="icon-btn btn-primary"
        type="button"
        title="Importer une photo et la convertir en patron"
        onclick={openFilePicker}
        disabled={importStatus === 'loading'}
      >
        <span class="glyph">⤓</span>
        <span class="label">{importStatus === 'loading' ? 'Chargement…' : 'Importer une photo'}</span>
      </button>
      <button
        class="icon-btn"
        type="button"
        title="Démarrer avec une grille vierge"
        onclick={openBlankDialog}
      >
        <span class="glyph">＋</span>
        <span class="label">Grille vierge</span>
      </button>
      <input
        bind:this={fileInputEl}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/*"
        onchange={onFileSelected}
        class="hidden-input"
        aria-label="Importer une image"
        tabindex="-1"
      />

      <span class="sep" aria-hidden="true"></span>

      <!-- Outil : 4 options -->
      <div class="seg" role="group" aria-label="Outil">
        <button
          class="seg-item"
          class:active={tool === 'paint'}
          type="button"
          aria-pressed={tool === 'paint'}
          title="Pinceau — peindre les cellules avec la couleur active"
          onclick={() => (tool = 'paint')}
        >
          Pinceau
        </button>
        <button
          class="seg-item"
          class:active={tool === 'bucket'}
          type="button"
          aria-pressed={tool === 'bucket'}
          title="Pot de peinture — remplir une zone de même couleur"
          onclick={() => (tool = 'bucket')}
        >
          Pot
        </button>
        <button
          class="seg-item"
          class:active={tool === 'eraser'}
          type="button"
          aria-pressed={tool === 'eraser'}
          title="Gomme — revenir à la couleur de fond"
          onclick={() => (tool = 'eraser')}
        >
          Gomme
        </button>
        <button
          class="seg-item"
          class:active={tool === 'pan'}
          type="button"
          aria-pressed={tool === 'pan'}
          title="Main — déplacer le canvas"
          onclick={() => (tool = 'pan')}
        >
          Main
        </button>
      </div>

      <!-- Taille de pinceau : visible pour pinceau et gomme -->
      {#if isDrawingTool()}
        <div class="cluster">
          <span class="cluster-label">Taille</span>
          <div class="seg" role="group" aria-label="Taille du pinceau">
            <button
              class="seg-item"
              class:active={brushSize === 1}
              type="button"
              aria-pressed={brushSize === 1}
              title="1 perle (point)"
              onclick={() => (brushSize = 1)}
            >
              1
            </button>
            <button
              class="seg-item"
              class:active={brushSize === 3}
              type="button"
              aria-pressed={brushSize === 3}
              title="3×3 perles"
              onclick={() => (brushSize = 3)}
            >
              3
            </button>
            <button
              class="seg-item"
              class:active={brushSize === 5}
              type="button"
              aria-pressed={brushSize === 5}
              title="5×5 perles"
              onclick={() => (brushSize = 5)}
            >
              5
            </button>
          </div>
        </div>
      {/if}

      <span class="sep" aria-hidden="true"></span>

      <!-- Historique : actions ponctuelles -->
      <div class="cluster" role="group" aria-label="Historique">
        <button
          class="icon-btn"
          type="button"
          disabled={!canUndo}
          title="Annuler (Ctrl+Z)"
          aria-label="Annuler"
          onclick={doUndo}
        >
          <span class="glyph">↶</span>
        </button>
        <button
          class="icon-btn"
          type="button"
          disabled={!canRedo}
          title="Rétablir (Ctrl+Y)"
          aria-label="Rétablir"
          onclick={doRedo}
        >
          <span class="glyph">↷</span>
        </button>
      </div>

      <span class="sep" aria-hidden="true"></span>

      <!-- Affichage des perles : segmented control -->
      <div class="cluster">
        <span class="cluster-label">Affichage</span>
        <div class="seg" role="group" aria-label="Mode d'affichage des perles">
          <button
            class="seg-item"
            class:active={mode === 'pearl'}
            type="button"
            aria-pressed={mode === 'pearl'}
            title="Vue perle réaliste (cercle avec trou)"
            onclick={() => (mode = 'pearl')}
          >
            Perle
          </button>
          <button
            class="seg-item"
            class:active={mode === 'flat'}
            type="button"
            aria-pressed={mode === 'flat'}
            title="Vue grille plate (carrés)"
            onclick={() => (mode = 'flat')}
          >
            Grille
          </button>
        </div>
      </div>

      <!-- Bords plaque : toggle binaire -->
      <button
        class="toggle-btn"
        class:on={showPlateBorders}
        type="button"
        aria-pressed={showPlateBorders}
        title="Afficher / masquer les bords de plaque en pointillés"
        onclick={() => (showPlateBorders = !showPlateBorders)}
      >
        <span class="check">{showPlateBorders ? '✓' : ''}</span>
        Bords plaque
      </button>

      <span class="sep" aria-hidden="true"></span>

      <!-- Fond du canvas : presets + color picker custom -->
      <div class="cluster">
        <span class="cluster-label">Fond</span>
        <div class="seg" role="group" aria-label="Couleur de fond du canvas">
          <button
            class="seg-item"
            class:active={canvasBgMode === 'white'}
            type="button"
            aria-pressed={canvasBgMode === 'white'}
            title="Fond blanc (réaliste plaque Hama)"
            onclick={() => (canvasBgMode = 'white')}
          >
            Blanc
          </button>
          <button
            class="seg-item"
            class:active={canvasBgMode === 'brand'}
            type="button"
            aria-pressed={canvasBgMode === 'brand'}
            title="Fond brandé (linen Perleur)"
            onclick={() => (canvasBgMode = 'brand')}
          >
            Brand
          </button>
          <button
            class="seg-item swatch-picker"
            class:active={canvasBgMode === 'custom'}
            type="button"
            aria-pressed={canvasBgMode === 'custom'}
            title="Choisir une couleur personnalisée"
            onclick={openColorPicker}
          >
            <span class="swatch-dot" style={`background: ${canvasBgCustom}`}></span>
            Custom
          </button>
          <input
            bind:this={colorInputEl}
            type="color"
            bind:value={canvasBgCustom}
            class="hidden-color"
            aria-label="Couleur de fond personnalisée"
            tabindex="-1"
          />
        </div>
      </div>

      <span class="sep" aria-hidden="true"></span>

      <!-- Vue : actions sur le viewport -->
      <button class="icon-btn" type="button" title="Recadrer la vue" onclick={fitView}>
        <span class="glyph">⤢</span>
        <span class="label">Recadrer</span>
      </button>

      <!-- Guide des perles : référence visuelle du catalogue, accessible à tout moment -->
      <button
        class="icon-btn"
        type="button"
        title="Consulter le catalogue complet des perles Hama Midi"
        onclick={() => (beadGuideOpen = true)}
      >
        <span class="glyph">☆</span>
        <span class="label">Guide perles</span>
      </button>

      <!-- Pose guidée : bascule en mode lecture aidée -->
      <button
        class="icon-btn icon-btn--accent"
        type="button"
        title="Démarrer la pose guidée — assistant pour réaliser physiquement le patron"
        onclick={enterGuidedMode}
      >
        <span class="glyph">▶</span>
        <span class="label">Pose guidée</span>
      </button>

      <!-- Export PDF -->
      <button
        class="icon-btn"
        type="button"
        title="Exporter le patron en PDF imprimable"
        onclick={() => (exportDialogOpen = true)}
      >
        <span class="glyph">⤴</span>
        <span class="label">Exporter PDF</span>
      </button>
    </div>
  </header>
  {/if}

  <div class="workspace">
    {#if editorMode === 'edit'}
    <aside class="palette is-desktop-only" class:palette--eraser={tool === 'eraser'} aria-label={tool === 'eraser' ? 'Filtre couleur pour la gomme' : 'Palette de couleurs'}>
      {#if tool === 'eraser'}
        <div class="palette-mode-notice" role="note">
          <strong class="palette-mode-notice__title">Mode gomme</strong>
          <span class="palette-mode-notice__hint">
            {activeColorIdx === EMPTY
              ? 'La gomme efface toutes les couleurs sous le pinceau.'
              : 'La gomme n\'efface que la couleur sélectionnée ci-dessous.'}
          </span>
        </div>
      {/if}
      <div class="palette-active">
        {#if activeColorIdx === EMPTY}
          <div class="active-dot active-dot--empty" aria-hidden="true"></div>
          <div class="active-meta">
            <strong>{tool === 'eraser' ? 'Toutes couleurs' : 'Aucune perle'}</strong>
            <small>{tool === 'eraser' ? 'Aucun filtre couleur' : 'Retire la perle (effet gomme)'}</small>
          </div>
        {:else}
          <div class="active-dot" style={`--bead-color: rgb(${activeColor?.rgb.r ?? 0}, ${activeColor?.rgb.g ?? 0}, ${activeColor?.rgb.b ?? 0})`}></div>
          <div class="active-meta">
            <strong>{tool === 'eraser' ? `Filtre : ${activeColor?.name ?? '—'}` : (activeColor?.name ?? '—')}</strong>
            <small>{tool === 'eraser' ? 'Efface uniquement cette couleur' : (activeColor?.id ?? '')}</small>
          </div>
        {/if}
      </div>
      <div class="palette-grid">
        <!-- Entrée spéciale : sémantique adaptée à l'outil actif.
             - Pinceau / Pot : peindre EMPTY = retirer la perle / vider la zone.
             - Gomme : retirer le filtre = effacer toutes les couleurs. -->
        <button
          type="button"
          class="swatch swatch--empty"
          class:active={activeColorIdx === EMPTY}
          title={tool === 'eraser'
            ? 'Aucun filtre — la gomme efface toutes les couleurs.'
            : 'Aucune perle — retire la perle. Avec le pot, vide toute la zone.'}
          aria-label={tool === 'eraser' ? 'Effacer toutes couleurs' : 'Aucune perle, mode gomme'}
          aria-pressed={activeColorIdx === EMPTY}
          onclick={() => (activeColorIdx = EMPTY)}
        >
          <span class="swatch-dot swatch-dot--empty" aria-hidden="true"></span>
          <span class="swatch-count">{tool === 'eraser' ? 'toutes' : 'vide'}</span>
        </button>
        {#each HAMA_DISPLAY as bead (bead.id)}
          {@const i = HAMA_CANON.get(bead.id) ?? 0}
          {@const isActive = i === activeColorIdx}
          {@const count = pattern.counts.get(bead.id) ?? 0}
          <button
            type="button"
            class="swatch"
            class:active={isActive}
            title={`${bead.name} (${bead.id}) — ${count} perles`}
            aria-label={`${bead.name}, ${count} perles utilisées`}
            onclick={() => (activeColorIdx = i)}
          >
            <span
              class="swatch-dot"
              style={`--bead-color: rgb(${bead.rgb.r}, ${bead.rgb.g}, ${bead.rgb.b})`}
            ></span>
            <span class="swatch-count">{count}</span>
          </button>
        {/each}
      </div>
    </aside>
    {/if}

    <div
      class="canvas-wrap"
      class:tool-paint={tool === 'paint' || tool === 'eraser'}
      class:tool-bucket={tool === 'bucket'}
      class:tool-pan={tool === 'pan'}
      bind:this={containerEl}
      style={`background: ${canvasBg}`}
    >
      <canvas bind:this={canvasEl}></canvas>
      <MiniMap
        {pattern}
        palette={HAMA}
        {viewport}
        {containerSize}
        cellSize={CELL_SIZE}
        onPan={onMiniMapPan}
      />
      <!-- Overlay mobile : FAB (création) + dock outils + sheet d'options.
           Masqué en desktop via .is-mobile-only (cf. modules/editor.css).
           Mais aussi masqué entièrement en mode pose guidée — la pose ne
           doit montrer aucun outil d'édition. -->
      {#if editorMode === 'edit'}
      <div class="mobile-overlay is-mobile-only" aria-hidden="false">
        <FabMenu onImport={openFilePicker} onBlank={openBlankDialog} />
        <MobileDock
          {pattern}
          palette={HAMA_DISPLAY}
          canonicalIndexOf={HAMA_CANON}
          {activeColorIdx}
          {tool}
          {brushSize}
          {canUndo}
          {canRedo}
          onSelectColor={(i) => (activeColorIdx = i)}
          onSelectTool={(t) => (tool = t)}
          onSelectBrushSize={(s) => (brushSize = s)}
          onUndo={doUndo}
          onRedo={doRedo}
          onExpand={() => (mobilePanelOpen = true)}
        />
        <MobilePanel
          open={mobilePanelOpen}
          {projectName}
          {mode}
          {showPlateBorders}
          {canvasBgMode}
          {canvasBgCustom}
          activeColor={activeColor}
          {tool}
          onClose={() => (mobilePanelOpen = false)}
          onChangeProjectName={(n) => (projectName = n)}
          onSelectMode={(m) => (mode = m)}
          onTogglePlateBorders={() => (showPlateBorders = !showPlateBorders)}
          onSelectBgMode={(m) => (canvasBgMode = m)}
          onChangeCustomBg={(hex) => (canvasBgCustom = hex)}
          onFitView={fitView}
          onExport={() => {
            mobilePanelOpen = false;
            exportDialogOpen = true;
          }}
          onOpenBeadGuide={() => {
            mobilePanelOpen = false;
            beadGuideOpen = true;
          }}
          onStartGuidedMode={() => {
            mobilePanelOpen = false;
            enterGuidedMode();
          }}
        />
      </div>
      {/if}
    </div>
  </div>

  {#if editorMode === 'guided'}
    <footer class="guide-strip" aria-label="Perles à poser sur la rangée courante">
      <div class="guide-strip__head">
        <span class="guide-strip__count">
          <strong>{currentLineBeads.length}</strong>
          {currentLineBeads.length > 1 ? 'perles' : 'perle'} sur cette
          {guidedAxis === 'row' ? 'ligne' : 'colonne'}
          {#if currentLineRuns.length > 0 && currentLineRuns.length !== currentLineBeads.length}
            <span class="guide-strip__runs">· {currentLineRuns.length} lots</span>
          {/if}
        </span>
        {#if currentLineBeads.length === 0}
          <span class="guide-strip__empty">(rangée vide)</span>
        {/if}
      </div>
      {#if currentLineRuns.length > 0}
        <ol class="bead-strip">
          {#each currentLineRuns as run (run.startPos)}
            {@const c = HAMA[run.idx]}
            <li class="bead-chip" class:bead-chip--run={run.count > 1}>
              <span class="bead-chip__pos">
                {#if run.count === 1}
                  {run.startPos}
                {:else}
                  {run.startPos}–{run.endPos}
                {/if}
              </span>
              {#if c}
                <span
                  class="bead-chip__dot"
                  style={`--c: rgb(${c.rgb.r},${c.rgb.g},${c.rgb.b})`}
                  title={`${c.name} (${c.id})`}
                ></span>
                <span class="bead-chip__name">{c.name}</span>
              {:else}
                <span class="bead-chip__dot bead-chip__dot--empty" title="vide"></span>
                <span class="bead-chip__name bead-chip__name--muted">vide</span>
              {/if}
              {#if run.count > 1}
                <span class="bead-chip__qty">×{run.count}</span>
              {/if}
            </li>
          {/each}
        </ol>
      {/if}
    </footer>
  {/if}

  <footer class="status">
    {#if importStatus === 'error'}
      <span class="error-msg">⚠ {importError}</span>
    {:else if editorMode === 'guided'}
      <!-- En pose guidée : juste les dimensions, le reste (outil, autosave,
           cloud) n'a pas de sens en lecture aidée et pollue la lecture. -->
      <span>{pattern.width} × {pattern.height} perles</span>
      <span class="dot">·</span>
      <span>{pattern.physicalSize.widthCm}×{pattern.physicalSize.heightCm} cm</span>
    {:else}
      <span>{pattern.width} × {pattern.height} perles</span>
      <span class="dot">·</span>
      <span>{pattern.physicalSize.widthCm}×{pattern.physicalSize.heightCm} cm</span>
      <span class="dot">·</span>
      <span>zoom {Math.round(viewport.scale * 100)} %</span>
      <span class="dot">·</span>
      <span>outil : {toolLabel(tool)}</span>
      {#if statusLabel(autosaveStatus)}
        <span class="dot">·</span>
        <span
          class="autosave-status"
          class:autosave-pending={autosaveStatus === 'pending' || autosaveStatus === 'saving'}
          class:autosave-error={autosaveStatus === 'error'}
        >
          {statusLabel(autosaveStatus)}
        </span>
      {/if}
    {/if}
    {#if editorMode === 'edit'}
      <span class="status-hint">
        {toolHint(tool)}
      </span>
    {/if}
  </footer>
</section>

<NewPatternDialog
  open={dialogOpen}
  mode={dialogMode}
  palette={HAMA}
  sourceImage={dialogSource}
  onCancel={onDialogCancel}
  onGenerate={onDialogGenerate}
/>

<BeadGuideDialog
  open={beadGuideOpen}
  palette={HAMA_DISPLAY}
  onClose={() => (beadGuideOpen = false)}
/>

<ExportDialog
  open={exportDialogOpen}
  {pattern}
  palette={HAMA}
  {projectName}
  onCancel={() => (exportDialogOpen = false)}
/>

